import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { routeTree } from './routeTree.gen'
import { initAnalytics, trackPageview } from './lib/analytics'
import { installTrackingFlushHandlers } from './lib/track'
import './styles/app.css'

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
  defaultStructuralSharing: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// --- Stale-chunk recovery -------------------------------------------------
// After a deploy, the hashed chunk names baked into an already-open tab no
// longer exist on the server (Vercel serves the index.html fallback instead),
// so a lazy import() throws "Failed to fetch dynamically imported module".
// Reload once to pull the fresh bundle. The guard clears on the next
// successful navigation, so a later deploy in the same session recovers too,
// while a genuinely missing chunk won't loop (onResolved never fires).
const STALE_CHUNK_KEY = 'sd-chunk-reloaded'
function recoverFromStaleChunk() {
  if (sessionStorage.getItem(STALE_CHUNK_KEY)) return
  sessionStorage.setItem(STALE_CHUNK_KEY, '1')
  window.location.reload()
}
window.addEventListener('vite:preloadError', event => {
  event.preventDefault()
  recoverFromStaleChunk()
})
window.addEventListener('unhandledrejection', event => {
  const message = String((event.reason && event.reason.message) || event.reason || '')
  if (/dynamically imported module|module script failed/i.test(message)) {
    recoverFromStaleChunk()
  }
})

initAnalytics()
installTrackingFlushHandlers()
router.subscribe('onResolved', ({ toLocation }) => {
  sessionStorage.removeItem(STALE_CHUNK_KEY)
  trackPageview(toLocation.pathname + toLocation.searchStr)
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1 },
  },
})

const rootElement = document.getElementById('app')
if (rootElement && !rootElement.innerHTML) {
  ReactDOM.createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
}
